import {forwardRef, useCallback, useImperativeHandle, useRef, useState} from "react";
import {generateArray} from "../../../utils/array-helper";
import {useServices} from "../../react-hooks/use-services.hook";
import {WheelInnerBorderBox} from "./boxes/borders/wheel-inner-border.box";
import {WheelOuterBorderBox} from "./boxes/borders/wheel-outer-border.box";
import {FlapperBox} from "./boxes/flapper/flapper.box";
import {WheelFrameBox} from "./boxes/frame/wheel-frame.box";
import {MidPinBox} from "./boxes/mid-pin/mid-pin.box";
import {WheelBox} from "./boxes/rotating-wheel/wheel.box";
import {WheelOfJackpotbetBox} from "./boxes/wheel-of-jackpotbet.box";
import {ConfettiComponent} from "./confetti/confetti.component";
import {HighlightedWinningSlice} from "./highlighted-winning-slice.component";
import {LightsOnWheel} from "./lights-on-wheel.component";
import {Prizes} from "./prizes.component";
import {WheelAmbientModeLights} from "./wheel-ambient-mode-lights.component";
import {WheelComponentProps, WheelRefType} from "./wheel-of-jackpotbet.component.interface";
import {WheelPegs} from "./wheel-pegs.component";

export const WheelOfJackpotbetComponent = forwardRef<WheelRefType, WheelComponentProps>(
    (props, ref) => {
        const {size, prizes, winningLights, winningConfetti, onWheelRotationEnd} = props;
        /** How many degrees the wheel should be rotated at the end of the animation. Gets computed by the winning prize index */
        const [degrees, setDegrees] = useState(0);
        /** Setting it to true starts the rotating animation. Setting it to false remove the animation thus reseting the wheel */
        const [isWheelRotated, setIsWheelRotated] = useState(false);
        /** Flags to start animation for winning (light strobes, confetti, slice highlight) */
        const [startLightsAnimation, setStartLightsAnimation] = useState(false);
        const [startConfettiAnimation, setStartConfettiAnimation] = useState(false);
        const [isWinningAnimationFired, setIsWinningAnimationFired] = useState(false);
        const [prizeIndex, setPrizeIndex] = useState<number | null>(null);

        const wheelElementRef = useRef<HTMLDivElement>(null);
        const pegRefs = useRef<({el: HTMLDivElement; prevPos: {left: number; top: number;}} | null)[]>(generateArray(prizes.length, null));
        /** Although this is used in rendering we don't need to put it in a state because we set the coordinates right before setting "setStartConfettiAnimation" state that controls also the display of the confetti */
        const confettiCenterCoordinates = useRef<undefined | {left: number; top: number;}>(undefined);

        // const audioRefs = useRef<HTMLAudioElement[]>(generateArray(prizes.length * 4, () => {
        //     return new Audio('https://eu-assets.contentstack.com/v3/assets/bltb3d7d79707153158/blt4a8488c489b0bbd6/66df393089b22352c932960f/click.mp3');
        // }));
        // const currentAudio = useRef(0);

        const services = useServices();

        useImperativeHandle(ref, () => {
            return {
                spinTheWheel(positionToLand: number) {
                    setIsWheelRotated(false);
                    setStartLightsAnimation(false);
                    setStartConfettiAnimation(false);
                    setIsWinningAnimationFired(false);
                    /**
                     * Formula:
                     * 3 complete rotations + offset to get to the desired position.
                     * The offset is the slice index * slice size
                     * Slice size is 360 / N
                     * => 3 * 360 + positionToLand * 360 / prizes.length => 1080 + positionToLand * 360 / prizes.length
                     */
                    const degreesToRotateWheel = 1080 + positionToLand * 360 / prizes.length;
                    setDegrees(degreesToRotateWheel);
                    setPrizeIndex(positionToLand);
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            setIsWheelRotated(true);
                        });
                    }, 100);
                },
            }
        });

        /**
         * This useEffect is disabled for the moment to check if it it has an impact of performance.
         */
        // useEffect(() => {
        //     const wheelDomRect = wheelElementRef.current.getBoundingClientRect();
        //     const wheelCenterCoordinates = {left: wheelDomRect.left + wheelDomRect.width / 2, top: wheelDomRect.top + wheelDomRect.height / 2};
        //     let animationFrameId = null;

        //     function pseudoAnimation() {
        //         pegRefs.current.forEach(peg => {
        //             if (peg) {
        //                 const pegDomRect = peg.el.getBoundingClientRect();
        //                 // the peg is small enough that we don't compute the center we just take the top and left
        //                 if (pegDomRect.left >= wheelCenterCoordinates.left && peg.prevPos.left < wheelCenterCoordinates.left) {
        //                     const leAudio = audioRefs.current[currentAudio.current % audioRefs.current.length]
        //                     // leAudio.pause();
        //                     leAudio.currentTime = 0.0;
        //                     leAudio.play();
        //                     currentAudio.current = (currentAudio.current + 1);
        //                 }
        //                 peg.prevPos = {
        //                     left: pegDomRect.left,
        //                     top: pegDomRect.top
        //                 };
        //             }
        //         });
        //         if (isWheelRotated && !isWinningAnimationFired) {
        //             animationFrameId = requestAnimationFrame(pseudoAnimation);
        //         }
        //     }
        //     if (isWheelRotated && !isWinningAnimationFired) {
        //         animationFrameId = requestAnimationFrame(pseudoAnimation);
        //     }
        //     return () => {
        //         if (animationFrameId) {
        //             cancelAnimationFrame(animationFrameId);
        //         }
        //     }
        // }, [prizes, isWheelRotated , isWinningAnimationFired]);

        const onPegRefCapture = useCallback((newPegRef: HTMLDivElement | null, idx: number) => {
            if (newPegRef) {
                const pegDomRect = newPegRef.getBoundingClientRect();
                pegRefs.current[idx] = {el: newPegRef, prevPos: {top: pegDomRect.top, left: pegDomRect.left}};
            } else {
                pegRefs.current[idx] = null;
            }
        }, []);

        const _onWheelRotationEnd = useCallback(() => {
            if (winningConfetti) {
                const wheelDomRect = wheelElementRef.current?.getBoundingClientRect();
                if (wheelDomRect) {
                    confettiCenterCoordinates.current = {left: wheelDomRect.left + wheelDomRect.width / 2, top: wheelDomRect.top + wheelDomRect.height / 2};
                } else {
                    confettiCenterCoordinates.current = undefined;
                }
                setStartConfettiAnimation(true);
            }
            if (onWheelRotationEnd) {
                onWheelRotationEnd();
            }
        }, [winningConfetti, onWheelRotationEnd]);

        return <WheelOfJackpotbetBox data-testid="wheel-component" className="wheel-component" ref={wheelElementRef} wheelSize={size}>
            <WheelFrameBox className="wheel-frame">
                {winningConfetti ? <ConfettiComponent animate={startConfettiAnimation} coordinates={confettiCenterCoordinates.current} /> : null}
                <LightsOnWheel startAnimation={startLightsAnimation} wheelSize={size} />
                <WheelOuterBorderBox className="outer-wheel-inner-border" />
                <WheelInnerBorderBox className="wheel-black-border" />
                <WheelAmbientModeLights />
            </WheelFrameBox>
            <WheelBox className="wheel"
                numberOfSlices={prizes.length} rotated={isWheelRotated} degrees={degrees}
                onAnimationEnd={() => {
                    setIsWinningAnimationFired(true);
                    if (winningLights) {
                        setStartLightsAnimation(true);
                    }
                }
            }>
                <HighlightedWinningSlice degrees={degrees} numberOfSlices={prizes.length} isWinningAnimationFired={isWinningAnimationFired} onWheelRotationEnd={_onWheelRotationEnd} />
                <WheelPegs totalPegs={prizes.length} onPegRefCapture={onPegRefCapture} />
                <Prizes prizes={prizes} wheelSize={size} highlightPrizeIndex={isWinningAnimationFired ? prizeIndex : null} />
            </WheelBox>
            <FlapperBox className="flapper">
                <img src={services.theme.currentTheme.assets.images.wheel_of_jackpotbet.flapper_png.toString()} alt="" />
            </FlapperBox>
            <MidPinBox className="mid-pin">
                <img src={services.theme.currentTheme.assets.images.wheel_of_jackpotbet.gold_center_png.toString()} alt="" />
            </MidPinBox>
        </WheelOfJackpotbetBox>;
    }
);
